/**
 * Which Speechmatics engine runs which language, for transcription and for
 * speaker separation.
 *
 * Speechmatics ships ONE ENGINE PER (language x version x tier) — 500+ ids in
 * all — so "the transcription engine" is not a value, it is a lookup. These
 * tables are that lookup for the languages Redact ships with; an organization
 * adds or replaces entries through the `transcriptionEngines` and
 * `speakerSeparationEngines` application configs, which are spread OVER these.
 *
 * Hard-coded here rather than left to configuration because the configuration
 * was the problem: before this, an org that wanted French had to find the
 * French engine id and type the map in itself, and the language dropdown was
 * built from whatever it typed — so an org with no map got no dropdown and no
 * languages at all. The ids are a fact about Speechmatics, the same in every
 * environment, and facts belong in the app.
 *
 * Keys are the `code` values in `./index.ts`, which is what the Select's
 * options and the job payload both use.
 *
 * TIERS. Each engine comes in `standard` and `enhanced`. Enhanced is the more
 * accurate, slower, more expensive model. The two switches below pick a tier
 * per engine TYPE rather than per language, so a language cannot end up
 * transcribed at one tier and separated at another by accident:
 *
 *   transcription      enhanced — accuracy is the product
 *   speaker separation standard — separation only has to draw boundaries, and
 *                      the timing comparisons that would justify enhanced over
 *                      standard have not been done
 *
 * Flip a switch and every language moves together. That is the point of having
 * it here rather than editing twelve ids.
 *
 * Version is Speechmatics v14.13.0 throughout. Taken from
 * veritone/engines @ engines/aiware/transcribe/speechmatics-chunk/cicd, where
 * each engine's `profile.yaml` carries its `_meta.engineId`; the folder names
 * (`fr-xscribe-enh-sm1413-cpu-…`) are the index into it. When a new version
 * lands, both tiers of both types move together or the tables disagree about
 * which version is current.
 *
 * A third table sits at the bottom: `RETIRED_TRANSCRIPTION_ENGINES`, the ids
 * these tables USED to hold. It exists because moving the default orphans
 * every transcript the old id produced — the language is recoverable only
 * through the engine, so an id that has left the table takes its transcript's
 * language with it. Read-only, and never part of selection; see its own
 * comment for why that line matters.
 */

/** Speechmatics v14.13.0 transcription, by language code. */
const TRANSCRIPTION_V14_13 = {
  enhanced: {
    en: '6e009536-6280-4461-8a80-713e2de29257',
    es: '3d32f962-8217-4f30-abf3-9e1dfe3ab3f1',
    fr: '64efc847-3120-414a-ab52-ad16e846b518',
  },
  standard: {
    en: 'a3f10d0b-5fd9-4317-b6ea-10d987a58853',
    es: 'b1dcfd9d-82cc-4904-b0c6-e08ae90f37b6',
    fr: 'aea91918-00f9-4362-9412-211a0a1d44ab',
  },
} as const;

/** Speechmatics v14.13.0 speaker separation, by language code. */
const SPEAKER_SEPARATION_V14_13 = {
  enhanced: {
    en: 'e9975cdf-1721-4946-bf9b-87812e254b96',
    es: '68714849-d60e-42da-8eed-6ed2600975b9',
    fr: '468ccb55-8b91-4bdc-924b-fae0b4024976',
  },
  standard: {
    en: '5af27bf4-c163-42d3-b121-0c5336a4978c',
    es: '829eccf5-6305-49b0-956c-a93aa13f988a',
    fr: 'f11ef636-0e04-48ff-b15a-0006a3facf43',
  },
} as const;

/** Accuracy is what transcription is for, so pay for the better model. */
const USE_ENHANCED_TRANSCRIPTION = true;

/**
 * Separation draws speaker boundaries that `mergeTranscriptionViewable` maps
 * onto words by midpoint; the enhanced model has not been shown to place them
 * better, so it is not worth its cost until it has.
 */
const USE_ENHANCED_SPEAKER_SEPARATION = false;

/** The language the app falls back to, and the one the scalar configs mean. */
export const DEFAULT_TRANSCRIPTION_LANGUAGE = 'en';

/**
 * A language-code to engine-id table that is GUARANTEED to cover the default
 * language. That guarantee is the type's whole job: `transcriptionEngineId`
 * and `speakerSeparationEngineId` are read all over the app as plain strings,
 * and they are now this map's default-language entry.
 */
export interface LanguageEngineMap {
  readonly [DEFAULT_TRANSCRIPTION_LANGUAGE]: string;
  readonly [code: string]: string;
}

export const BUILT_IN_TRANSCRIPTION_ENGINES: LanguageEngineMap =
  USE_ENHANCED_TRANSCRIPTION
    ? TRANSCRIPTION_V14_13.enhanced
    : TRANSCRIPTION_V14_13.standard;

export const BUILT_IN_SPEAKER_SEPARATION_ENGINES: LanguageEngineMap =
  USE_ENHANCED_SPEAKER_SEPARATION
    ? SPEAKER_SEPARATION_V14_13.enhanced
    : SPEAKER_SEPARATION_V14_13.standard;

/**
 * The built-in table, with the org's application config spread over it and the
 * scalar engine-id config applied to the default language.
 *
 * Precedence, lowest to highest: the built-in table, then the scalar
 * (`transcriptionEngineId` / `speakerSeparationEngineId`) which means "the
 * default language's engine" and nothing more, then the per-language map.
 * An org that sets both gets the map for the languages it names and the scalar
 * for the default language, which is the only reading under which setting both
 * is not a contradiction.
 *
 * Entries are only ever added or replaced, never removed: an org cannot take
 * a language away by configuring it. Which languages are OFFERED is a separate
 * question, answered by `engineAvailability` against the environment.
 */
export const resolveLanguageEngines = (
  builtIn: LanguageEngineMap,
  scalarEngineId: string | undefined,
  configuredMap: Record<string, string> | null | undefined
): LanguageEngineMap => ({
  ...builtIn,
  ...(configuredMap || {}),
  // Written last and resolved inline rather than spread in order: it puts the
  // default language's three-way precedence in one expression, and it is what
  // lets the result satisfy `LanguageEngineMap` without a cast.
  [DEFAULT_TRANSCRIPTION_LANGUAGE]:
    configuredMap?.[DEFAULT_TRANSCRIPTION_LANGUAGE] ??
    scalarEngineId ??
    builtIn[DEFAULT_TRANSCRIPTION_LANGUAGE],
});

/**
 * Speechmatics engines Redact USED to run, by language code — READ ONLY.
 *
 * The language a transcript was produced in is recorded nowhere: the only
 * trace is which engine ran, so `languageOfTranscriptionEngine` reads the
 * engine id back through the current table. That works until the table moves.
 * When the default went to v14.13.0 every transcript already in the estate was
 * produced by an id no longer in it, so the lookup returned `undefined` and
 * the Run Engines dialog reported transcribed media as untranscribed.
 *
 * So: the TRANSCRIPTION ids we have shipped before, kept so an old transcript
 * can still say what language it is in.
 *
 * Transcription only, deliberately. Every caller passes the engine behind a
 * TRANSCRIPT — `existingTranscriptEngineId` / `transcriptEngineId` — so a
 * separation id could never be looked up here, and listing one would only
 * suggest it does something. Separation's language is not its own: it is read
 * from the transcript it groups.
 *
 * NEVER read this to CHOOSE an engine — a retired id must not be reachable by
 * a new job. It feeds the language lookup and nothing else; selection goes
 * through `resolveLanguageEngines` above.
 *
 * INCOMPLETE BY NATURE. It covers the en/es/fr engines Veritone deployed, so
 * an org that configured an engine of its own is not in it, and callers must
 * still handle `undefined` — see `replacesExistingTranscript`, which treats an
 * unknown language as "this run replaces something" rather than assuming it
 * replaces nothing.
 *
 * Source: the Speechmatics license-rotation inventory (id, product, version,
 * language) in vpe-specs `user/lnozot/sm-license-rotation/`, filtered to the
 * languages Redact ships and to ids no longer in the tables above. Names are
 * kept beside each id so the next person can tell what they are looking at.
 */
export const RETIRED_TRANSCRIPTION_ENGINES: Readonly<Record<string, string>> = {
  // Speechmatics Transcription Enhanced (v9.1.0) — en
  'e97d1564-39ff-4016-a034-e1f32aa9eb7d': 'en',
  // Speechmatics Transcription (v9.2.0) — en
  'bbb11671-e21e-4e0f-a790-2ef1841ee554': 'en',
  // Speechmatics Transcription (v10.7.0) — en
  '5dd41983-3035-4d01-b9e7-f587dbc69f88': 'en',
  // Speechmatics Transcription Enhanced (v10.7.0) — en
  '03f3ac99-72aa-46e7-a98e-5faae8e58560': 'en',
  // Speechmatics Transcription (v11.0.1) — en
  '41fe5e9b-08d8-4b03-9903-ed0ebd697039': 'en',
  // Speechmatics Transcription Enhanced (v11.0.1) — en
  '6fad6b3a-b3b3-4e97-92db-abf944fc1f33': 'en',
  // Speechmatics Transcription (v11.0.1) — es
  '9f774c9d-c919-48c2-8678-3ab5897e17b9': 'es',
  // Speechmatics Transcription Enhanced (v11.0.1) — es
  '49bcf01c-95df-4051-8354-9f0fffbf9f21': 'es',
  // Speechmatics Transcription (v12.0.1) — en
  'def70e07-029c-4ec7-8918-35b0385a8a87': 'en',
  // Speechmatics Transcription Enhanced (v12.0.1) — en
  '01e54769-c448-4ece-a500-4fab5b49b722': 'en',
  // Speechmatics Transcription (v12.0.1) — es
  '28b4015e-b573-4046-977b-22e512e867a7': 'es',
  // Speechmatics Transcription Enhanced (v12.0.1) — es
  '1be23bb3-7297-47d7-8ad3-430e9ecc041e': 'es',
  // Speechmatics Transcription (v12.0.1) — fr
  'c28a3a95-a620-4ec9-a21b-c29fc429f96d': 'fr',
  // Speechmatics Transcription Enhanced (v12.0.1) — fr
  'd90d57d6-f258-4895-8182-b93ba428c62b': 'fr',
  // Speechmatics Transcription Enhanced (v13.4.0) — en
  'c9c6b7b2-30eb-4125-b537-be5506109f5c': 'en',
  // Speechmatics Transcription (v13.4.0) — es
  'd5848ac6-8e7d-487a-b9ac-9f82f5e104ac': 'es',
  // Speechmatics Transcription Enhanced (v13.4.0) — es
  '199dbef6-abf9-494c-a076-8a82a21e062d': 'es',
};
